import { Box, ButtonBase, IconButton, Link, styled, Typography } from '@mui/material';
import React, { ReactNode, useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { ICrumbAdditionalData, ITicketTrailEvent } from '@tymely/atoms';
import CommentIcon from '@mui/icons-material/Comment';
import EditAttributesIcon from '@mui/icons-material/EditAttributes';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonIcon from '@mui/icons-material/Person';
import RuleIcon from '@mui/icons-material/Rule';
import SearchIcon from '@mui/icons-material/Search';
import { useClipboard } from 'use-clipboard-copy';
import { LlmTrailEvent } from '@tymely/atoms/llmTrail.atoms';
import JsonView from '@tymely/components/JsonView';
import AgentBox, { _Expanded, _JsonContainer } from '@tymely/features/Ticket/TicketLayout/Conversation/AgentBox';

import { formatBubbleDate, isTicketTrailEvent } from './utils';

const IconTag = (props: { text: string; icon: ReactNode; title?: string }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }} title={props.title}>
        <Box sx={{ height: 14, overflow: 'visible', display: 'flex', alignItems: 'center' }}>{props.icon}</Box>
        <span>{props.text}</span>
    </Box>
);

const EVENT_DETAILS: ((data: ICrumbAdditionalData) => ReactNode)[] = [
    ({ decision }) =>
        decision?.title ? (
            <IconTag text={decision.title} title="Decision" icon={<RuleIcon sx={{ fontSize: 15 }} />} />
        ) : (
            false
        ),
];

const _Root = styled(Typography)`
    display: flex;
    flex-direction: column;
    border-bottom: 1px solid #ddd;
`;

const _Header = styled(Box)`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
`;

const _Label = styled('strong', { shouldForwardProp: (prop: string) => !['isEmptyData'].includes(prop) })<{
    isEmptyData: boolean;
}>`
    ${({ isEmptyData }) => !isEmptyData && 'cursor: pointer;'};
`;

const _Error = styled(ErrorIcon)`
    display: 'inline';
    font-size: 16;
`;

const _IconButton = styled(IconButton, { shouldForwardProp: (prop: string) => !['isEmptyData'].includes(prop) })<{
    isEmptyData: boolean;
}>`
    visibility: ${({ isEmptyData }) => (isEmptyData ? 'hidden' : 'visible')};
`;

const _Filler = styled(Box)`
    flex: 1;
`;

type EventItemProps = {
    event: ITicketTrailEvent | LlmTrailEvent;
};

const EventItem = ({ event }: EventItemProps) => {
    const { copy } = useClipboard();

    const getEventTags = (event: ITicketTrailEvent): ReactNode[] => {
        const {
            comment_id,
            additional_data: { username, policy_type, lb_type, logic_box_id, argument_name, arg_name, search_term },
        } = event;
        const _arg_name = arg_name || argument_name;

        return [
            username ? <IconTag key="username" text={username} icon={<PersonIcon sx={{ fontSize: 15 }} />} /> : false,
            comment_id ? (
                <ButtonBase key="comment-id" centerRipple sx={{ p: 0 }} onClick={() => copy(String(comment_id))}>
                    <span>
                        <IconTag text={String(comment_id)} icon={<CommentIcon sx={{ fontSize: 15 }} />} />
                    </span>
                </ButtonBase>
            ) : (
                false
            ),
            policy_type ? <span key="policy-type">{policy_type}</span> : false,
            lb_type ? <span key="lb-type">{lb_type}</span> : false,
            logic_box_id ? <span key="logic-box-id">{logic_box_id}</span> : false,
            _arg_name ? (
                <IconTag
                    key="arg-name"
                    text={_arg_name}
                    title="Argument name"
                    icon={<EditAttributesIcon sx={{ fontSize: 25 }} />}
                />
            ) : (
                false
            ),
            search_term ? (
                <IconTag
                    key="search-term"
                    text={search_term}
                    title="Search term"
                    icon={<SearchIcon sx={{ fontSize: 15 }} />}
                />
            ) : (
                false
            ),
        ].filter(Boolean);
    };

    const [isExpanded, setIsExpanded] = useState(false);

    const _isTicketTrailEvent = isTicketTrailEvent(event);
    const eventData = _isTicketTrailEvent
        ? event.additional_data
        : {
              raw_response: event.raw_response,
              prompt: event.prompt,
              request: event.request,
              response: event.response,
          };
    const isEmptyData = useMemo(() => isEmpty(eventData), [eventData]);
    const isBigData = useMemo(() => JSON.stringify(eventData).length > 1000, [eventData]);
    const title = _isTicketTrailEvent ? event.operation : event?.request?.template_name;
    const details = useMemo(() => EVENT_DETAILS.map((item) => item(eventData)).filter(Boolean), [eventData]);

    return (
        <_Root variant="caption" color="GrayText">
            {!_isTicketTrailEvent && title?.startsWith('AGENT_') && <AgentBox event={event as LlmTrailEvent} />}
            <_Header>
                {_isTicketTrailEvent && event.status !== 'SUCCESS' && <_Error color="warning" />}
                <_Label onClick={() => !isEmptyData && setIsExpanded((val) => !val)} isEmptyData={isEmptyData}>
                    {title}
                </_Label>
                {_isTicketTrailEvent && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{getEventTags(event)}</Box>
                )}
                {!_isTicketTrailEvent && (
                    <Link
                        component="button"
                        onClick={() => {
                            const url = `prompt-editor?prompt=${(event as LlmTrailEvent).prompt_template?.name}&version=${(event as LlmTrailEvent).prompt_template?.id}`;
                            window.open(url, '_blank', 'noopener,noreferrer');
                        }}
                        underline="hover"
                        color="primary"
                        sx={{ fontSize: 14, fontWeight: 500 }}
                    >
                        Open Prompt Editor
                    </Link>
                )}
                <_Filler />
                <Box>{formatBubbleDate(`${event.created_date}Z`, false, true)}</Box>
                <_IconButton isEmptyData={isEmptyData} onClick={() => !isEmptyData && setIsExpanded((val) => !val)}>
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </_IconButton>
            </_Header>
            {isExpanded ? (
                <_Expanded>
                    <_JsonContainer>
                        <JsonView data={eventData} shouldExpandNode={() => !isBigData} />
                    </_JsonContainer>
                </_Expanded>
            ) : (
                !isEmpty(details) && (
                    <_Expanded>
                        {details.map((item, i) => (
                            <Box key={i}>{item}</Box>
                        ))}
                    </_Expanded>
                )
            )}
        </_Root>
    );
};

export default EventItem;
